import { MenuItem, Stack, TextField } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { MEDIA_PLACEMENT_TYPE_ARRAY, MediaPlacementType } from '@sr/dto';
import { SimpleParamFilter } from 'shared/ui/param-filters/simple-param-filter';
import { InputParamFilter } from 'shared/ui/param-filters/input-param-filter';
import { useMediaTypesDropdown } from 'entities/media/media-type-picker/dropdown.hook';
import { CityParamFilter } from 'entities/geo/cities/city-filter/city-param-filter';
import { CountryParamFilter } from 'entities/geo/countries/country-filter/country-param-filter';
import { RegionParamFilter } from 'entities/geo/regions/region-filter/region-param-filter';
import { SimpleSelectUrlParamsFilter } from 'entities/filters/simple-select-url-params-filter';
import { FilterWrapper, ValueFormatterContext } from 'shared/ui/filter/filter-wrapper';
import { useCitiesDropdown } from 'entities/geo/cities/city-picker/hook';
import useCountries from 'entities/geo/countries/countries.hook';

const HIERARCHY_OPTIONS = [
  { label: 'Все', value: '' },
  { label: 'Материнское', value: 'parent' },
  { label: 'Дочернее', value: 'child' },
  { label: 'Без связи', value: 'none' }
];

type MediaListFilterProps = {
  withNameSearch?: boolean;
  withMediaTypeSearch?: boolean;
  withCitiesSearch?: boolean;
  withGeoFieldSearch?: boolean;
  withHierarchyFilter?: boolean;
  withPricesFilter?: boolean;
}
  
export const MediaListFilter = ({ withNameSearch = false, withMediaTypeSearch = true, withCitiesSearch = false, withGeoFieldSearch = true, withHierarchyFilter = true, withPricesFilter = true }: MediaListFilterProps) => {
  const mediaItems = useMediaTypesDropdown();
  const MEDIA_TYPES = mediaItems.items.map((item) => ({ label: item.name, id: item.id }));

  const [searchParams, setSearchParams] = useSearchParams();
  const { list: cities } = useCitiesDropdown(Boolean(searchParams.get('cityId')));
  const { list: countries } = useCountries();

  const placementLabelMap = new Map(MEDIA_PLACEMENT_TYPE_ARRAY.map((item) => [String(item.id), item.label]));

  const hierarchyLabelMap = new Map(HIERARCHY_OPTIONS.map((item) => [item.value, item.label]));

  const placementValue = searchParams.get('placement') ?? '';
  const selectedPlacement = Object.values(MediaPlacementType).includes(placementValue as MediaPlacementType)
    ? (placementValue as MediaPlacementType)
    : '';

  const handlePlacementChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams);

    ['regionId', 'cityId', 'placement'].forEach(param => newParams.delete(param));

    if (Object.values(MediaPlacementType).includes(value as MediaPlacementType)) {
      newParams.set('placement', value);
    }

    setSearchParams(newParams, { replace: true });
  };

  const handleHierarchyChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams);

    ['hierarchy'].forEach(param => newParams.delete(param));

    if (value) {
      newParams.set('hierarchy', value);
    }

    setSearchParams(newParams, { replace: true });
  };

  const createOptionFormatter = (options: { id: number | string; label: string }[]) => {
    const map = new Map(options.map((item) => [String(item.id), item.label]));
    return ({ value }: ValueFormatterContext) => map.get(String(value)) ?? value;
  };

  const mediaTypeFormatter = createOptionFormatter(MEDIA_TYPES);
  const hierarchyFormatter = ({ value }: ValueFormatterContext) => hierarchyLabelMap.get(String(value)) ?? value;
  const pricesFormatter = createOptionFormatter([
    { id: 'false', label: 'Без прайсов' },
    { id: 'true', label: 'С прайсами' },
  ]);

  const cityFormatter = ({ value }: ValueFormatterContext) => {
    const city = cities.find((item) => String(item.id) === value);
    return city ? city.name : value;
  };

  const countryFormatter = ({ value }: ValueFormatterContext) => {
    const country = countries.find((item) => String(item.id) === value);
    return country ? country.name : value;
  };

  const renderGeoField = () => {
    switch (selectedPlacement) {
    case MediaPlacementType.FEDERAL:
      return null;
    case MediaPlacementType.REGIONAL:
      return <RegionParamFilter queryParamName="regionId" key="region" />;
    case MediaPlacementType.LOCAL:
      return <CityParamFilter queryParamName="cityId" key="city"/>;
    default:
      return <TextField label={'Тип не выбран'} disabled fullWidth/>;
    }
  };

  return (
    <FilterWrapper title="Фильтры СМИ">
      {withNameSearch && (
        <FilterWrapper.Item label="Название" queryKeys="title">
          <InputParamFilter queryParamName="title" label="Название" />
        </FilterWrapper.Item>
      )}

      {withGeoFieldSearch && (
        <FilterWrapper.Item
          label="География"
          queryKeys={['placement', 'countryId', 'regionId', 'cityId']}
          summaryFormatter={({ searchParams }) => {
            const placement = searchParams.get('placement');
            const countryId = searchParams.get('countryId');
            const regionId = searchParams.get('regionId');
            const cityId = searchParams.get('cityId');

            if (!placement && !countryId && !regionId && !cityId) return 'География';

            const placementLabel = placement ? placementLabelMap.get(placement) ?? placement : '';
            const cityLabel = cityId ? cityFormatter({ value: cityId, key: 'cityId', searchParams }) : '';
            const regionLabel = regionId ? regionId : '';
            const countryLabel = countryId ? countryFormatter({ value: countryId, key: 'countryId', searchParams }) : '';

            const locationLabel = cityLabel || regionLabel || countryLabel;

            if (placementLabel && locationLabel) return `${placementLabel}: ${locationLabel}`;
            if (locationLabel) return `География: ${locationLabel}`;
            return `География: ${placementLabel || '-'}`;
          }}
        >
          <Stack spacing={1.5}>
            <TextField
              select
              label="Тип размещения"
              value={selectedPlacement}
              onChange={(e) => handlePlacementChange(e.target.value)}
              fullWidth
            >
              <MenuItem value={''}>Все типы</MenuItem>
              {MEDIA_PLACEMENT_TYPE_ARRAY.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <CountryParamFilter queryParamName="countryId" key="country" />
            {renderGeoField()}
          </Stack>
        </FilterWrapper.Item>
      )}

      {withCitiesSearch && (
        <FilterWrapper.Item label="Город" queryKeys="cityId" valueFormatter={cityFormatter}>
          <CityParamFilter queryParamName="cityId" />
        </FilterWrapper.Item>
      )}

      {withMediaTypeSearch && (
        <FilterWrapper.Item label="Тип СМИ" queryKeys="mediaTypeId" valueFormatter={mediaTypeFormatter}>
          <SimpleParamFilter queryParamName="mediaTypeId" items={MEDIA_TYPES} label="Тип СМИ" />
        </FilterWrapper.Item>
      )}

      {withPricesFilter && (
        <FilterWrapper.Item label="Наличие прайсов" queryKeys="withPrices" valueFormatter={pricesFormatter}>
          <SimpleSelectUrlParamsFilter
            queryParamName='withPrices'
            items={[{value: 'false', label: 'Без прайсов'}, {value: 'true', label: 'С прайсами'}]}
            label='Наличие прайсов'
          />
        </FilterWrapper.Item>
      )}

      {withHierarchyFilter && (
        <FilterWrapper.Item label="Тип связи" queryKeys="hierarchy" valueFormatter={hierarchyFormatter}>
          <TextField
            select
            label="Тип связи"
            value={searchParams.get('hierarchy') || ''}
            onChange={(e) => handleHierarchyChange(e.target.value)}
            fullWidth
          >
            {HIERARCHY_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </FilterWrapper.Item>
      )}
    </FilterWrapper>
  );
};
